import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio,
  QrCode,
  Search,
  DollarSign,
  PoundSterling,
  Euro,
  Music,
  Check,
  TrendingUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { getMinimumPrice, getLocaleForCurrency } from '@/lib/currencyConfig';

/** Demo checkout uses 30% platform fee on top of song price (matches £3 + £0.90 = £3.90). */
function buildLandingDemoPricing(currency = 'GBP') {
  const code = (currency || 'GBP').toUpperCase();
  const min = getMinimumPrice(code);
  const fee = Math.round(min * 0.3 * 100) / 100;
  const total = Math.round((min + fee) * 100) / 100;
  const thirdSong = Math.round((min + 1) * 100) / 100;
  const earnings = Math.round(min * 15 * 100) / 100;
  const locale = getLocaleForCurrency(code);
  const fmt = (amount) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  return {
    currency: code,
    min,
    fee,
    total,
    fmt,
    songPrices: [min, min, thirdSong],
    earnings,
  };
}

function getMoneyIconComponent(currency) {
  const c = (currency || 'GBP').toUpperCase();
  if (c === 'GBP') return PoundSterling;
  if (c === 'EUR') return Euro;
  return DollarSign;
}

const PhoneFrame = ({ children, innerClassName = 'bg-gray-900' }) => (
  <div className="bg-black rounded-[2.2rem] p-2 shadow-[0_25px_80px_rgba(15,23,42,0.7)] relative z-10 border border-gray-800">
    <div className={`rounded-[1.5rem] w-[200px] h-[420px] p-4 overflow-hidden border border-white/5 relative ${innerClassName}`}>
      {/* Notch */}
      <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-50">
        <div className="w-24 h-5 bg-black rounded-b-xl"></div>
      </div>
      <div className="pt-4 h-full flex flex-col">
        {children}
      </div>
    </div>
  </div>
);

const buildQrMatrix = (size = 21) => {
  const matrix = Array.from({ length: size }, () => Array(size).fill(false));
  const drawFinder = (row, col) => {
    for (let r = 0; r < 7; r++) {
      for (let c = 0; c < 7; c++) {
        const isBorder = r === 0 || r === 6 || c === 0 || c === 6;
        const isCenter = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        matrix[row + r][col + c] = isBorder || isCenter;
      }
    }
  };
  drawFinder(0, 0);
  drawFinder(0, size - 7);
  drawFinder(size - 7, 0);

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (matrix[r][c]) continue;
      const pattern = ((r * 3 + c * 5) % 7 === 0) || ((r + c) % 11 === 0);
      matrix[r][c] = pattern;
    }
  }
  return matrix;
};

const GoLivePhoneSlide = ({ demo }) => (
  <div className="flex flex-col items-center gap-6">
    <PhoneFrame innerClassName="bg-gray-950">
      <div className="flex flex-col h-full text-slate-100">
        <div className="flex items-center justify-between mb-4 pt-2">
          <span className="text-sm font-bold">Artist Dashboard</span>
          <div className="flex items-center gap-2 text-[10px] text-gray-400 bg-gray-800 px-2 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            Offline
          </div>
        </div>

        <div className="bg-gray-900 rounded-xl p-3 mb-4 border border-gray-800">
          <p className="text-xs text-gray-400 mb-1">Current Settings</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">Base Price</span>
              <span className="text-xs text-blue-400 font-bold">{demo.fmt(demo.min)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">Available Songs</span>
              <span className="text-xs text-blue-400 font-bold">142</span>
            </div>
          </div>
        </div>

        <div className="space-y-2 text-xs text-gray-300">
          {[
            'Queue Management Enabled',
            'Payments Ready'
          ].map((item, idx) => (
            <motion.div
              key={item}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + idx * 0.15 }}
              className="flex items-center gap-2 bg-gray-900 rounded-lg px-3 py-2 border border-gray-800"
            >
              <Check className="w-3.5 h-3.5 text-blue-500" />
              <span>{item}</span>
            </motion.div>
          ))}
        </div>

        <div className="mt-auto pb-2">
          <motion.button
            initial={{ scale: 0.95 }}
            animate={{ scale: [0.95, 1, 0.95] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-full bg-blue-600 text-sm text-white font-bold rounded-xl py-3 shadow-[0_0_15px_rgba(37,99,235,0.4)] flex items-center justify-center gap-2"
          >
            <Radio className="w-4 h-4" />
            GO LIVE
          </motion.button>
        </div>
      </div>
    </PhoneFrame>
  </div>
);

const AudienceQRSlide = () => {
  const qrMatrix = useMemo(() => buildQrMatrix(), []);

  return (
    <div className="flex flex-col lg:flex-row items-center gap-8 relative z-10">
      <div className="relative bg-white text-gray-900 rounded-[2rem] p-6 shadow-2xl w-[220px]">
        <div className="absolute -top-4 -right-4 bg-blue-600 text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1 text-white shadow-lg transform rotate-6">
          <QrCode className="w-3 h-3" />
          SCAN ME
        </div>
        <div className="text-center mb-4">
          <p className="font-bold text-lg leading-tight">Request a Song!</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">Point your camera</p>
        </div>
        <div className="bg-gray-100 rounded-2xl p-2.5 border border-gray-200">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${qrMatrix.length}, minmax(0, 1fr))`,
              gap: '1px'
            }}
          >
            {qrMatrix.map((row, rowIdx) =>
              row.map((active, colIdx) => (
                <div
                  key={`${rowIdx}-${colIdx}`}
                  className={`aspect-square rounded-sm ${active ? 'bg-gray-900' : 'bg-transparent'}`}
                />
              ))
            )}
          </div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-[10px] text-gray-500">or visit</p>
          <p className="text-xs font-bold text-blue-600">ysr.to/johndoe</p>
        </div>
      </div>

      <PhoneFrame innerClassName="bg-black relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1516280440614-376394488844?q=80&w=600&auto=format&fit=crop" className="w-full h-full object-cover opacity-40 blur-[2px]" alt="Background" />
        </div>
        <div className="relative flex flex-col h-full text-white z-10 pt-4">
          <div className="text-center mb-6">
            <p className="text-sm font-semibold">Camera</p>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative w-36 h-36 rounded-2xl overflow-hidden mb-6">
              <div className="absolute inset-0 border-2 border-yellow-400/80 rounded-2xl z-20">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-yellow-400"></div>
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-yellow-400"></div>
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-yellow-400"></div>
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-yellow-400"></div>
              </div>
              <div className="absolute inset-0 bg-yellow-400/10 z-10" />
              <motion.div
                animate={{ y: [-140, 140] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-x-0 h-1 bg-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.8)] z-30"
              />
            </div>
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="bg-yellow-400 text-black text-[10px] font-bold px-4 py-2 rounded-full flex items-center gap-2 cursor-pointer shadow-lg"
            >
              Open "ysr.to/johndoe" <ChevronRight className="w-3 h-3" />
            </motion.div>
          </div>
        </div>
      </PhoneFrame>
    </div>
  );
};

const AudienceSearchSlide = ({ demo }) => {
  const [showArtistPhone, setShowArtistPhone] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowArtistPhone(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const songs = [
    { title: 'Wonderwall', artist: 'Oasis', price: demo.fmt(demo.songPrices[0]) },
    { title: 'Sweet Caroline', artist: 'Neil Diamond', price: demo.fmt(demo.songPrices[1]) },
    { title: "Don't Stop Believin'", artist: 'Journey', price: demo.fmt(demo.songPrices[2]) }
  ];

  return (
    <div className="flex flex-col lg:flex-row items-center gap-6 relative z-10">
      <motion.div initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5 }}>
        <div className="text-center mb-2">
          <span className="text-xs font-bold text-gray-400 tracking-wider uppercase">Fan Phone</span>
        </div>
        <PhoneFrame innerClassName="bg-gray-900">
          <div className="flex flex-col h-full text-white">
            <div className="text-center mb-4 pt-2">
              <h3 className="font-bold text-sm">John Doe</h3>
              <p className="text-[10px] text-gray-400">Playing live now</p>
            </div>
            <motion.div
              initial={{ width: '80%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-gray-800 rounded-xl p-2 mb-3 text-left text-xs text-gray-300 flex items-center gap-2 border border-gray-700"
            >
              <Search className="w-4 h-4 text-gray-400" />
              Wonderwall...
            </motion.div>
            <div className="space-y-2 overflow-hidden flex-1">
              {songs.map((song, i) => (
                <motion.div
                  key={song.title}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8 + i * 0.15 }}
                  className={`rounded-xl p-2.5 border flex items-center justify-between ${
                    i === 0 ? 'bg-blue-900/40 border-blue-500/50' : 'bg-gray-800 border-gray-700'
                  }`}
                >
                  <div className="truncate pr-2">
                    <p className="text-xs font-bold truncate">{song.title}</p>
                    <p className="text-[10px] text-gray-400 truncate">{song.artist}</p>
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-bold shrink-0 ${
                    i === 0 ? 'text-blue-400' : 'text-gray-300'
                  }`}>
                    {song.price}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </PhoneFrame>
      </motion.div>

      <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
        <div className="text-center mb-2">
          <span className="text-xs font-bold text-blue-400 tracking-wider uppercase">Artist Dashboard</span>
        </div>
        <PhoneFrame innerClassName="bg-gray-950">
          <AnimatePresence mode="wait">
            {showArtistPhone ? (
              <motion.div
                key="artist-phone"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col h-full text-white"
              >
                <div className="flex items-center justify-between mb-4 pt-2">
                  <div className="flex items-center gap-2 text-[10px] text-gray-300 bg-red-500/20 text-red-400 px-2 py-1 rounded-full font-bold">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    LIVE
                  </div>
                  <span className="text-xs font-bold">Queue</span>
                </div>
                
                <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-2 font-semibold">Incoming Requests</div>
                
                <motion.div 
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ type: 'spring', damping: 200 }}
                  className="bg-blue-600/20 rounded-xl p-3 border border-blue-500/50 mb-3 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                  <div className="flex justify-between items-start mb-1">
                    <p className="text-sm font-bold text-white">Wonderwall</p>
                    <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded font-bold">+{demo.fmt(demo.min)}</span>
                  </div>
                  <p className="text-[10px] text-gray-300 mb-2">Oasis</p>
                  <div className="flex gap-2 mt-2">
                    <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold py-1.5 rounded">Accept</button>
                  </div>
                </motion.div>
                
                <div className="bg-gray-900 rounded-xl p-3 border border-gray-800 opacity-60">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-bold text-gray-300">Sweet Caroline</p>
                    <span className="text-[10px] text-gray-400">{demo.fmt(demo.min)}</span>
                  </div>
                  <p className="text-[10px] text-gray-500">Neil Diamond</p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="h-full rounded-2xl border border-dashed border-gray-700 flex flex-col items-center justify-center text-gray-500 text-xs gap-3"
              >
                <Radio className="w-8 h-8 text-gray-600 animate-pulse" />
                <p>Waiting for requests...</p>
              </motion.div>
            )}
          </AnimatePresence>
        </PhoneFrame>
      </motion.div>
    </div>
  );
};

const PaidRequestsSlide = ({ demo }) => {
  const PayIcon = getMoneyIconComponent(demo.currency);
  return (
  <div className="flex flex-col lg:flex-row items-center gap-6 relative z-10">
    <div className="text-center">
      <span className="text-xs font-bold text-gray-400 tracking-wider uppercase block mb-2">Fan Phone</span>
      <PhoneFrame innerClassName="bg-gray-900">
        <div className="h-full flex flex-col pt-2 text-white">
          <div className="text-center mb-6">
            <h3 className="font-bold text-sm">Checkout</h3>
          </div>
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-gray-800 rounded-xl p-4 mb-auto border border-gray-700"
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="font-bold text-sm">Wonderwall</p>
                <p className="text-[10px] text-gray-400">Oasis</p>
              </div>
              <Music className="w-5 h-5 text-blue-400" />
            </div>
            
            <div className="space-y-2 text-xs border-t border-gray-700 pt-3">
              <div className="flex justify-between text-gray-300">
                <span>Song Request</span>
                <span>{demo.fmt(demo.min)}</span>
              </div>
              <div className="flex justify-between text-gray-300">
                <span>Platform Fee</span>
                <span>{demo.fmt(demo.fee)}</span>
              </div>
              <div className="flex justify-between font-bold text-sm pt-2">
                <span>Total</span>
                <span className="text-blue-400">{demo.fmt(demo.total)}</span>
              </div>
            </div>
          </motion.div>
          
          <motion.button
            animate={{
              boxShadow: [
                '0 0 0 0 rgba(59, 130, 246, 0.4)',
                '0 0 0 8px rgba(59, 130, 246, 0)',
              ]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-lg mb-2"
          >
            <PayIcon className="w-4 h-4" />
            Pay {demo.fmt(demo.total)}
          </motion.button>
        </div>
      </PhoneFrame>
    </div>

    <div className="text-center">
      <span className="text-xs font-bold text-blue-400 tracking-wider uppercase block mb-2">Artist Dashboard</span>
      <PhoneFrame innerClassName="bg-gray-950">
        <div className="flex flex-col h-full text-white pt-2">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1, type: 'spring' }}
            className="bg-blue-600 text-white rounded-xl p-3 mb-6 shadow-[0_4px_20px_rgba(37,99,235,0.4)] flex items-center gap-3 z-20 relative"
          >
            <div className="bg-white/20 p-1.5 rounded-full">
              <PayIcon className="w-4 h-4 text-white" />
            </div>
            <div className="text-left">
              <p className="text-xs font-bold">Payment Received!</p>
              <p className="text-[10px] text-blue-100">+{demo.fmt(demo.min)} • Wonderwall</p>
            </div>
          </motion.div>

          <div className="flex-1">
            <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-2 font-semibold flex justify-between">
              <span>Up Next</span>
              <span className="text-blue-400">1 song</span>
            </div>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 1.5 }}
              className="bg-gray-900 rounded-xl p-3 border-l-2 border-blue-500 border-y border-r border-y-gray-800 border-r-gray-800"
            >
              <div className="flex justify-between items-start mb-1">
                <p className="text-sm font-bold">Wonderwall</p>
                <div className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded text-[10px] font-bold flex items-center gap-1">
                  <Check className="w-3 h-3" /> Paid
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mb-2">Oasis</p>
              <button className="w-full bg-gray-800 hover:bg-gray-700 text-white text-[10px] font-bold py-1.5 rounded border border-gray-700">
                Mark as Played
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.8 }}
            className="bg-gray-900 rounded-xl p-3 text-center border border-gray-800 mt-auto mb-2"
          >
            <p className="text-[10px] text-gray-400 mb-1 uppercase tracking-wider">Tonight's Earnings</p>
            <p className="text-2xl font-bold text-white flex items-center justify-center gap-1">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              {demo.fmt(demo.earnings)}
            </p>
          </motion.div>
        </div>
      </PhoneFrame>
    </div>
  </div>
  );
};

const ArtistFlowAnimations = ({ currency = 'GBP' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  const demo = useMemo(() => buildLandingDemoPricing(currency), [currency]);
  const MoneyIcon = useMemo(() => getMoneyIconComponent(currency), [currency]);

  const steps = useMemo(
    () => [
      {
        icon: Radio,
        title: 'Go Live',
        description: 'Start your session from your phone in seconds. Set your request price and take control of the queue.',
        animation: <GoLivePhoneSlide demo={demo} />
      },
      {
        icon: QrCode,
        title: 'Fans Scan QR',
        description: 'Audience scans your unique QR code or visits your link. No app download required for them.',
        animation: <AudienceQRSlide />
      },
      {
        icon: Search,
        title: 'Browse & Request',
        description: 'Fans browse your custom setlist, search for their favourite songs, and send requests directly to you.',
        animation: <AudienceSearchSlide demo={demo} />
      },
      {
        icon: MoneyIcon,
        title: 'Get Paid',
        description: 'Accept paid requests instantly. Watch your earnings grow on your live dashboard throughout the gig.',
        animation: <PaidRequestsSlide demo={demo} />
      }
    ],
    [demo, MoneyIcon]
  );

  const totalSteps = steps.length;

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting && !hasEnteredViewport) {
          setHasEnteredViewport(true);
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasEnteredViewport]);

  useEffect(() => {
    if (!hasEnteredViewport || !isInView || !isAutoPlaying) return undefined;

    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % totalSteps);
    }, 6000);

    return () => clearInterval(interval);
  }, [hasEnteredViewport, isInView, totalSteps, isAutoPlaying]);

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentStep((prev) => (prev + 1) % totalSteps);
  };

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentStep((prev) => (prev - 1 + totalSteps) % totalSteps);
  };

  const handleStepClick = (index) => {
    setIsAutoPlaying(false);
    setCurrentStep(index);
  };

  return (
    <div ref={containerRef} className="w-full max-w-7xl mx-auto py-8">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
        
        {/* Left Side: Bullet Points */}
        <div className="w-full lg:w-1/3 flex flex-col gap-3 z-20 relative">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 hidden lg:block">How It Works</h3>
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            return (
              <button
                key={index}
                onClick={() => handleStepClick(index)}
                className={`flex items-start text-left p-4 rounded-2xl transition-all duration-300 border ${
                  isActive 
                    ? 'bg-gray-800/80 border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.15)] scale-[1.02]' 
                    : 'bg-gray-900/50 border-transparent hover:bg-gray-800/60 opacity-60 hover:opacity-100'
                }`}
              >
                <div className={`p-2.5 rounded-full mr-4 shrink-0 transition-colors duration-300 ${isActive ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]' : 'bg-gray-800 text-gray-400'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className={`text-base font-bold transition-colors duration-300 ${isActive ? 'text-white' : 'text-gray-300'}`}>
                    {step.title}
                  </h4>
                  <p className={`mt-1 text-xs md:text-sm transition-colors duration-300 ${isActive ? 'text-gray-300' : 'text-gray-500'}`}>
                    {step.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Right Side: Graphic with Arrows */}
        <div className="w-full lg:w-2/3 relative flex flex-col items-center justify-center min-h-[500px]">
          <button 
            onClick={handlePrev}
            className="absolute left-0 lg:-left-6 z-30 p-3 bg-gray-800 hover:bg-gray-700 text-white rounded-full border border-gray-600 transition-colors shadow-xl hidden sm:flex items-center justify-center hover:scale-110"
            aria-label="Previous Step"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <div className="w-full relative flex items-center justify-center">
            {/* Background glowing blob */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-600/20 blur-[100px] rounded-full pointer-events-none"></div>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full flex items-center justify-center"
              >
                {steps[currentStep]?.animation}
              </motion.div>
            </AnimatePresence>
          </div>

          <button 
            onClick={handleNext}
            className="absolute right-0 lg:-right-6 z-30 p-3 bg-gray-800 hover:bg-gray-700 text-white rounded-full border border-gray-600 transition-colors shadow-xl hidden sm:flex items-center justify-center hover:scale-110"
            aria-label="Next Step"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Mobile Navigation (below graphic) */}
          <div className="flex sm:hidden items-center justify-center gap-6 mt-8 w-full z-20">
            <button 
              onClick={handlePrev}
              className="p-3 bg-gray-800 text-white rounded-full border border-gray-600 shadow-lg active:scale-95"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2">
              {steps.map((_, i) => (
                <div 
                  key={i} 
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentStep ? 'bg-blue-500 w-6' : 'bg-gray-600'}`} 
                />
              ))}
            </div>
            <button 
              onClick={handleNext}
              className="p-3 bg-gray-800 text-white rounded-full border border-gray-600 shadow-lg active:scale-95"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ArtistFlowAnimations;

